import React, { useEffect, useState } from 'react';
import type { RateLimitInfo } from '../types';

interface RateLimitBannerProps {
  info: RateLimitInfo;
}

const SCOPE_TITLE: Record<RateLimitInfo['scope'], string> = {
  burst: 'Slow down a moment',
  daily: 'Daily limit reached',
  global: 'Service is taking a breather',
};

const SCOPE_TONE: Record<RateLimitInfo['scope'], 'warn' | 'bad'> = {
  burst: 'warn',
  daily: 'bad',
  global: 'bad',
};

function useCountdown(resetAt: number): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!resetAt || resetAt <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [resetAt]);

  if (!resetAt) return '';
  const diffMs = resetAt - now;
  if (diffMs <= 0) return 'now — refresh and try again';
  const totalSec = Math.ceil(diffMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `in ${h}h ${m}m`;
  if (m > 0) return `in ${m}m ${s.toString().padStart(2, '0')}s`;
  return `in ${s}s`;
}

export const RateLimitBanner: React.FC<RateLimitBannerProps> = ({ info }) => {
  const countdown = useCountdown(info.resetAt);
  const tone = SCOPE_TONE[info.scope];
  const title = SCOPE_TITLE[info.scope];

  const bg = tone === 'warn' ? 'var(--score-mid-wash)' : 'var(--score-low-wash)';
  const fg = tone === 'warn' ? 'var(--score-mid)' : 'var(--score-low)';
  const border = tone === 'warn' ? 'rgba(181,132,42,0.3)' : 'rgba(179,58,58,0.3)';
  const pillCls = tone === 'warn' ? 'pill-warning' : 'pill-critical';

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        marginTop: 16,
        padding: '16px 20px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 4,
        color: 'var(--ink)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <span className={`pill ${pillCls}`}>Rate limited</span>
        <strong style={{ fontSize: 15, color: fg, fontWeight: 600 }}>{title}</strong>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--ink-2)' }}>{info.message}</p>
      {countdown && (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-3)' }}>Try again {countdown}.</p>
      )}
    </div>
  );
};
